import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { compact, countBy, isEmpty, mapValues, noop, omitBy, isUndefined } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { TableList, Page, PageEmptyState, PageErrorState, FilterPickerGroup, PageContentHeader, Spinner, ConfirmationModal, Typography, DropdownMenu, ChipButton, Button, useStateInRef, DropdownMenuItem } from 'seagull';
import { ChevronDown, ChevronUp, Download, EmailSent, Import } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { getAgreementPdfUrl } from '~/business-logic/relationshipLogic';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import TableFilters from '~/components/TableFilters/TableFilters';
import { ErrorText } from '~/constants';
import { useOverlayContext, useIoContext, useStore, useStoreActions } from '~/contexts';
import { useDuplicateProposal } from '~/hooks/reactQuery/mutations/useDuplicateProposal';
import { useRedirectToProposal } from '~/hooks/useCreateAgreement';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useEnterAccountantAccess } from '~/hooks/useEnterAccountantAccess';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useOnMount } from '~/hooks/useOnMount';
import { useOpenEditAgreementModal } from '~/hooks/useOpenEditAgreementModal';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useTerminateAgreement } from '~/hooks/useTerminateAgreement';
import { useBulkActionModal } from '~/hooks/useTerminateAgreement/useBulkActionModal';
import { useVerifyPlaidBankAccount } from '~/hooks/useVerifyPlaidBankAccount';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise, handleRequest } from '~/infra/utils';
import { relationshipModel, relationshipSummaryModel } from '~/models';
import { PlaidBankAccountStatusTypes } from '~/models/payment-method';
import { useSubscribeToBillingObservable } from '~/providers/BillingObservableProvider';
import { BusinessChannelEventBinder, PaymentMethodEventType, PusherEvent } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import { useAppConfig, useStoreStatic } from '~/store/store-context';
import { helpArticles } from '~/utils/helpArticles';
import pluralize from '~/utils/pluralize';
import { buildRelationshipsParamsFilter } from '~/utils/relationships-params-filter-builder';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '../../components/PageLoadingState';
import SeeWhatItsLike from '../../components/SeeWhatItsLike/SeeWhatItsLike';
import styles from './AgreementsDashboard.module.scss';
import { sortAgreements, HeaderColId } from './AgreementsDashboard.utils';
import { serializeFilter, filtersConfig, FilterType, deserializeFiltersFromURL, defaultAgreementStatusFilter, getTableFilterLabels, getTableFilterGroups, makeTableFilterConfig, formatFilterDescription, agreementFilterTextsPath } from './agreementsDashboardFilters';
import AgreementsDashboardRow from './AgreementsDashboardRow';
import ChangeAssigneeModal from './ChangeAssigneeModal';
import { NoAgreementsIcon } from './NoAgreementsIcon';
import RenameAgreementModal from './RenameAgreementModal';
import img1 from './SeeWhatItIsLikeImages/1.png';
import img2 from './SeeWhatItIsLikeImages/2.png';
const title = 'Agreements';
const relationshipLoader = 'getRelationship';
function getTableDefs(isVendor) {
    const effectiveDateLabel = 'Effective date';
    const defs = compact([
        {
            headerItem: {
                id: HeaderColId.companyName,
                value: 'Company name'
            },
            columnDef: {
                width: 220,
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_company_name'
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: HeaderColId.status,
                value: 'Status'
            },
            columnDef: {
                width: 128,
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_status'
                }
            },
            defaultSortDirection: 'asc'
        },
        isVendor && {
            headerItem: {
                id: HeaderColId.assignee,
                value: 'Assignee'
            },
            columnDef: {
                width: [
                    130,
                    160
                ],
                sortable: true,
                bi: {
                    name: 'sort_assignee'
                }
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: HeaderColId.effectiveDate,
                value: effectiveDateLabel
            },
            columnDef: {
                width: [
                    112,
                    120
                ],
                sortable: true,
                bi: {
                    name: 'sort_effective_date'
                },
                mobile: {
                    label: {
                        text: effectiveDateLabel
                    }
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: HeaderColId.paymentMethod,
                value: 'Payment method'
            },
            columnDef: {
                width: 190,
                sortable: true,
                bi: {
                    name: 'sort_payment_method'
                }
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: HeaderColId.lastBilled,
                value: 'Last Billed'
            },
            columnDef: {
                width: 100,
                sortable: true,
                bi: {
                    name: 'sort_last_billed'
                }
            },
            defaultSortDirection: 'desc'
        }
    ]);
    const sortOptions = defs.flatMap((d, colIndex)=>d.columnDef.sortable ? [
            {
                id: d.headerItem.id,
                label: String(d.headerItem.value),
                defaultDirection: d.defaultSortDirection,
                colIndex
            }
        ] : []);
    return {
        columnDefs: defs.map((d)=>d.columnDef),
        headerItems: defs.map((d)=>d.headerItem),
        sortOptions
    };
}
export const AgreementsDashboardTestIds = pathgen('AgreementsDashboardTestIds');
export function ActualAgreementsDashboard(props) {
    const { data: _data, isRefreshing, search, filters, onChangeFilters, refresh, onChangeSearch } = props;
    const [data, setData] = useState(_data);
    useEffect(()=>setData(_data), [
        _data
    ]);
    const overlayContext = useOverlayContext();
    const { openNewTab } = useIoContext();
    const isVendor = useStore(selectors.user.isVendor);
    const { inputDebounceTime } = useAppConfig();
    const businessesWithAccountantAccess = useStore(selectors.user.businessesWithAccountantAccess);
    const pdfHost = useStore(selectors.pdfHost);
    const { api, logBI } = useStoreActions();
    const openEditModeModal = useOpenEditAgreementModal();
    const { headerItems, columnDefs, sortOptions } = useMemo(()=>getTableDefs(isVendor), [
        isVendor
    ]);
    const sortIds = useMemo(()=>headerItems.map((item)=>item.id), [
        headerItems
    ]);
    const defaultSort = useMemo(()=>({
            colIndex: 3,
            sort: 'desc'
        }), []);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const isMobileDevice = useIsMobileForHomePage();
    const { redirectToProposal } = useRedirectToProposal();
    const { duplicateProposal } = useDuplicateProposal({
        onSuccess: (newDraftId)=>redirectToProposal('agreement', newDraftId),
        withPageLoader: true
    });
    const { terminateAgreementById } = useTerminateAgreement();
    const enterAccountantAccess = useEnterAccountantAccess();
    const verifyPlaidBankAccount = useVerifyPlaidBankAccount();
    const scrollElRef = useRef(null);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11252, 0);
    const { doBulkAction } = useBulkActionModal();
    const { getState } = useStoreStatic();
    const { ON_DEMAND } = useFeatureFlags();
    const businessId = useStore(selectors.user.businessId);
    const { t: agreementFilterTexts } = useLocaleKeys(agreementFilterTextsPath);
    const [serviceNameOptions, setServiceNameOptions] = useState([]);
    const tableFilterConfig = useMemo(()=>makeTableFilterConfig({
            serviceNameOptions,
            onDemandEnabled: !!ON_DEMAND,
            texts: agreementFilterTexts
        }), [
        serviceNameOptions,
        ON_DEMAND,
        agreementFilterTexts
    ]);
    const filterLabels = useMemo(()=>getTableFilterLabels(agreementFilterTexts.filterLabels), [
        agreementFilterTexts
    ]);
    const filterGroups = useMemo(()=>getTableFilterGroups(agreementFilterTexts.filterGroupLabels), [
        agreementFilterTexts
    ]);
    const [tableSelectedFilters, setTableSelectedFilters] = useState(()=>{
        const initial = {};
        for (const type of Object.values(FilterType)){
            if (filtersConfig[type].hasValue(filters[type])) {
                initial[type] = filters[type];
            }
        }
        return initial;
    });
    const mobileSelectedFilters = useMemo(()=>{
        const result = {};
        for (const type of Object.values(FilterType)){
            if (filtersConfig[type].hasValue(filters[type])) {
                result[type] = filters[type];
            }
        }
        return result;
    }, [
        filters
    ]);
    const [selected, setSelected] = useState({});
    const getSelected = useStateInRef(selected);
    const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    const agreements = useMemo(()=>{
        return sortAgreements({
            agreements: data,
            sort,
            isVendor,
            headerItems
        });
    }, [
        data,
        sort,
        isVendor,
        headerItems
    ]);
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: agreements,
        scrollElRef
    });
    const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
    useOnMount(()=>{
        if (businessId) {
            api.relationship.getServiceNames(businessId, isVendor, 'agreement').then((names)=>{
                setServiceNameOptions(names.map((n)=>({
                        label: n,
                        value: n
                    })));
            });
        }
    });
    useEffect(()=>{
        // this unselects any agreement that is not found in the newly fetched list of agreements
        if (!data || isEmpty(getSelected())) return;
        const ids = data.map((agreement)=>agreement.id);
        const newSelected = mapValues(getSelected(), (origValue, agreementId)=>ids.includes(agreementId) ? origValue : false);
        setSelected(newSelected);
    }, [
        data
    ]);
    const onToggleAllSelection = useCallback((selectAll)=>{
        setSelected(agreements.reduce((acc, param)=>{
            let { id } = param;
            return Object.assign(acc, {
                [id]: selectAll
            });
        }, {}));
    }, [
        agreements
    ]);
    function onVerifyPaymentMethod(agreement) {
        if (!agreement.fundingSource) return;
        if (agreement.fundingSource.isPlaidVerification()) {
            verifyPlaidBankAccount({
                fundingSourceId: agreement.fundingSource.id(),
                relationshipId: agreement.id,
                onVerify () {
                    // Optimistic UI.
                    const newData = data.map((r)=>r.id === agreement.id ? relationshipSummaryModel.setFundingSourcePlaidVerificationStatus(r, PlaidBankAccountStatusTypes.ManuallyVerified) : r);
                    setData(newData);
                },
                onVerifyFailed () {
                    // Optimistic UI.
                    const newData = data.map((r)=>r.id === agreement.id ? relationshipSummaryModel.setFundingSourcePlaidVerificationStatus(r, PlaidBankAccountStatusTypes.VerificationFailed) : r);
                    setData(newData);
                }
            });
        }
    }
    const onSortChange = (sort)=>{
        logBI(11250, {
            column: sort ? sortIds[sort.colIndex] : 'none'
        });
        changeSort(sort);
    };
    const onClearSearch = ()=>{
        onChangeSearch('');
        debouncedLogSearchToBI.onClear();
    };
    const onDownloadPDF = (agreement)=>{
        openNewTab(getAgreementPdfUrl(agreement.id, pdfHost));
    };
    const onTerminateAgreement = (agreement)=>{
        terminateAgreementById({
            id: agreement.id,
            postAction: ()=>{
                refresh();
            }
        });
    };
    const onEditAgreement = (agreement)=>{
        openEditModeModal({
            relationshipId: agreement.id,
            onUpdate: refresh
        });
    };
    const onDuplicateAgreement = (agreement)=>{
        duplicateProposal({
            relationshipId: agreement.id,
            draftId: agreement.draftId
        });
    };
    const handleTableFiltersRefresh = (newFilters, sort)=>{
        const amendments = newFilters[FilterType.HasAmendments];
        if (Array.isArray(amendments) && amendments.length > 1) {
            newFilters = _object_spread_props(_object_spread({}, newFilters), {
                [FilterType.HasAmendments]: []
            });
        }
        const changes = {};
        for (const type of Object.values(FilterType)){
            const hasNew = type in newFilters;
            if (!hasNew) {
                if (filtersConfig[type].hasValue(filters[type])) {
                    changes[type] = filtersConfig[type].getEmptyValue();
                }
            } else {
                changes[type] = newFilters[type];
            }
        }
        onChangeFilters(changes);
        changeSort(sort);
    };
    const handleSetTableSelectedFilters = (updater)=>{
        setTableSelectedFilters((prev)=>{
            const next = typeof updater === 'function' ? updater(prev) : updater;
            const amendments = next[FilterType.HasAmendments];
            if (Array.isArray(amendments) && amendments.length > 1) {
                return _object_spread_props(_object_spread({}, next), {
                    [FilterType.HasAmendments]: []
                });
            }
            return next;
        });
    };
    function getSelectedAgreements() {
        return Object.entries(selected).filter((param)=>{
            let [, isSelected] = param;
            return isSelected;
        }).map((param)=>{
            let [agreementId, _] = param;
            return agreementId;
        });
    }
    async function bulkRequestPaymentMethod(selectedAgreements) {
        logBI(11254, {
            count: selectedAgreements.length
        });
        await doBulkAction({
            bulkItemIds: selectedAgreements,
            confirmation: {
                title: 'Request Payment Method',
                primaryButtonLabel: 'Send Request',
                content: /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            children: [
                                'By selecting "Send Request", all clients of the selected',
                                ' ',
                                /*#__PURE__*/ _jsx("strong", {
                                    children: selectedAgreements.length
                                }),
                                " agreements will receive an email asking them to set up their payment details in Anchor."
                            ]
                        }),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(Typography, {
                            children: "Consider sending a personal message to clients still transitioning to Anchor to communicate the purpose of this request."
                        }),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(ExternalLinkButton, {
                            openInNewTab: true,
                            href: helpArticles.requestPaymentMethod(),
                            children: "Learn more about Request Payment Method"
                        })
                    ]
                })
            },
            progressTitle: 'Request Payment Method',
            actionName: 'sent',
            itemName: 'payment method request',
            onConfirmed: ()=>{
                logBI(11255, {
                    count: selectedAgreements.length
                });
            },
            canProcessItem: (itemId)=>agreements.find((a)=>a.id === itemId && !relationshipModel.isTerminated(a)) != null,
            processSingleItem: (agreementId)=>handlePromise(api.relationship.requestPaymentMethod(agreementId)),
            onDone: (failedAgreementIds)=>{
                setSelected(failedAgreementIds.reduce((acc, agreementId)=>_object_spread_props(_object_spread({}, acc), {
                        [agreementId]: true
                    }), {}));
            }
        });
    }
    async function onRenameAgreement(agreement, onSuccess) {
        overlayContext.startLoader(relationshipLoader);
        const relationship = await handleRequest(api.relationship.get(agreement.id));
        overlayContext.stopLoader(relationshipLoader);
        if (!relationship.success) {
            overlayContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'get-agr-error');
        } else {
            overlayContext.showModal({
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (p)=>/*#__PURE__*/ _jsx(RenameAgreementModal, _object_spread_props(_object_spread({}, p), {
                        relationship: relationship.data,
                        onSuccess: onSuccess
                    }))
            });
        }
    }
    const openImportAgreementsModal = ()=>{
        overlayContext.showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    textAlignCenter: false,
                    primaryButtonLabel: "Close",
                    onPrimaryButtonClick: dismissModal,
                    onCloseButtonClick: dismissModal,
                    title: "Import agreements",
                    primaryButtonTestId: "close-import-agreements-modal",
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: "Need to import agreements into Anchor that were signed elsewhere?"
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                        openInNewTab: true,
                                        href: "https://forms.gle/Q9sMHXFcp5Fs866FA",
                                        children: "Complete this form"
                                    }),
                                    ' ',
                                    "and our team will reach out with the next steps."
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                style: {
                                    marginTop: '16px'
                                },
                                children: "Please note: This service is currently available for requests of 30 agreements or more."
                            })
                        ]
                    })
                });
            }
        });
    };
    function changeAssignee(agreement) {
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (p)=>/*#__PURE__*/ _jsx(ChangeAssigneeModal, _object_spread({
                    relationship: agreement,
                    onSuccess: refresh
                }, p))
        });
    }
    const exportToCsv = ()=>{
        const mgmtHost = selectors.mgmtHost(getState());
        const filterParams = buildRelationshipsParamsFilter({
            type: 'agreement',
            isVendor,
            search,
            filters
        });
        const cleanedFilterParams = omitBy(filterParams, isUndefined);
        const searchParams = new URLSearchParams(cleanedFilterParams).toString();
        openNewTab("".concat(mgmtHost, "/business/").concat(businessId, "/relationships-summary-mv/export-csv?").concat(searchParams));
    };
    const renderEmptyPage = ()=>/*#__PURE__*/ _jsx(Page, {
            header: title,
            loading: isRefreshing,
            variant: "containedHeight",
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                maxHeight: true,
                testId: "agreements-empty",
                illustration: /*#__PURE__*/ _jsx(NoAgreementsIcon, {}),
                title: "Make it official with an online agreement",
                message: "The Anchor agreement can always be updated, is accessible online, and serves as a single source of truth between you and your clients.",
                actions: isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SeeWhatItsLike, {
                            steps: [
                                {
                                    title: 'A single place to maintain client information',
                                    subtitle: 'All aspects of your agreement and relationship history live here, online, equally visible to you and your client.',
                                    image: {
                                        src: img1,
                                        width: 1246,
                                        height: 748
                                    }
                                },
                                {
                                    title: 'Hands-free relationship management ',
                                    subtitle: 'You and your clients will be updated on any changes or required action.',
                                    image: {
                                        src: img2,
                                        width: 1246,
                                        height: 748
                                    }
                                }
                            ],
                            testId: "agreements-dashboard"
                        }),
                        /*#__PURE__*/ _jsx(CreateProposalButton, {
                            hidePrefix: true,
                            testId: "agreements-empty-new-btn"
                        })
                    ]
                })
            })
        });
    const renderAgreementsPage = ()=>/*#__PURE__*/ _jsx(Page, {
            testId: "agreements-dashboard",
            header: title,
            ref: scrollElRef,
            actions: isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(DropdownMenu, {
                        testId: AgreementsDashboardTestIds.MoreActions.Root(),
                        target: /*#__PURE__*/ _jsx(Button, {
                            skin: "primaryOutline",
                            testId: AgreementsDashboardTestIds.MoreActions.Button(),
                            suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {}),
                            children: "More Actions"
                        }),
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                testId: AgreementsDashboardTestIds.MoreActions.ImportAgreementsBtn(),
                                iconStart: /*#__PURE__*/ _jsx(Download, {}),
                                onClick: ()=>openImportAgreementsModal(),
                                bi: {
                                    name: 'import_agreements'
                                },
                                children: "Import agreements"
                            }),
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                intent: Intent.ExportAgreementsToCsv,
                                iconStart: /*#__PURE__*/ _jsx(Import, {}),
                                testId: AgreementsDashboardTestIds.MoreActions.ExportAgreementsBtn(),
                                onClick: exportToCsv,
                                bi: {
                                    name: 'export_agreements'
                                },
                                children: "Export agreements"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CreateProposalButton, {}, "new-agreement-button")
                ]
            }),
            onScroll: onScroll,
            children: /*#__PURE__*/ _jsxs(TableList.Provider, {
                hasActionColumn: true,
                enableRowSelection: isVendor,
                columnDefs: columnDefs,
                sort: sort,
                selectedRows: selected,
                onSortChange: onSortChange,
                onSelectionChange: setSelected,
                numRows: agreements.length,
                numSelectedRows: numSelected,
                onToggleAllSelection: onToggleAllSelection,
                children: [
                    /*#__PURE__*/ _jsxs(PageContentHeader, {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.agreementsFilters,
                                children: /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                                    flexWrap: true,
                                    testId: "agreements-dashboard-filter-bar",
                                    children: [
                                        /*#__PURE__*/ _jsx(TableFilters, {
                                            testIdPrefix: "agreements-dashboard",
                                            search: {
                                                value: search,
                                                testId: 'agreements-dashboard-search',
                                                registerHotkey: registerSearchBoxHotkey,
                                                debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
                                                onChange: (event)=>{
                                                    const searchStr = event.target.value;
                                                    onChangeSearch(searchStr);
                                                    debouncedLogSearchToBI.onChange(searchStr);
                                                },
                                                onClear: onClearSearch
                                            },
                                            filterLabels: filterLabels,
                                            filterConfig: tableFilterConfig,
                                            filterGroups: filterGroups,
                                            selectedFilters: isMobileDevice ? mobileSelectedFilters : tableSelectedFilters,
                                            setSelectedFilters: isMobileDevice ? noop : handleSetTableSelectedFilters,
                                            onRefresh: handleTableFiltersRefresh,
                                            onInputFocus: ()=>setRegisterSearchBoxHotkey(false),
                                            onInputBlur: ()=>setRegisterSearchBoxHotkey(true),
                                            mobile: {
                                                formatFilterDescription: (filter, value)=>formatFilterDescription({
                                                        filterType: filter,
                                                        value,
                                                        texts: agreementFilterTexts
                                                    }),
                                                sortOptions
                                            }
                                        }),
                                        isVendor && !isMobileDevice && /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Typography, {
                                                    className: styles.selectedItems,
                                                    testId: "agreement-dashboard-bulk-counter",
                                                    children: numSelected > 0 ? "".concat(pluralize('agreement', numSelected, true), " selected") : ''
                                                }),
                                                /*#__PURE__*/ _jsx(DropdownMenu, {
                                                    onOpenChange: setBulkActionsOpen,
                                                    target: /*#__PURE__*/ _jsx(ChipButton, {
                                                        testId: "agreement-dashboard-bulk-actions",
                                                        suffixIcon: bulkActionsOpen ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {}),
                                                        highlight: bulkActionsOpen,
                                                        disabled: numSelected === 0,
                                                        children: "Bulk actions"
                                                    }),
                                                    children: /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                                        iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
                                                        testId: "agreement-dashboard-bulk-request-payment-method",
                                                        onClick: ()=>bulkRequestPaymentMethod(getSelectedAgreements()),
                                                        disabled: !isVendor,
                                                        bi: {
                                                            name: 'bulk_request_payment_method'
                                                        },
                                                        children: "Request payment method"
                                                    })
                                                })
                                            ]
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph1",
                                weight: "light",
                                testId: AgreementsDashboardTestIds.TableHeader.AgreementsCounter(),
                                className: styles.agreementsCounter,
                                children: pluralize('agreement', data.length, true)
                            }),
                            /*#__PURE__*/ _jsx(TableList.Header, {
                                items: headerItems,
                                testId: "agreements-dashboard-table-header"
                            })
                        ]
                    }),
                    isRefreshing ? /*#__PURE__*/ _jsx("div", {
                        className: styles.loading,
                        "data-testid": "agreements-table-loading",
                        children: /*#__PURE__*/ _jsx(Spinner, {})
                    }) : agreements.length ? paginatedData.map((agreement, index)=>/*#__PURE__*/ _jsx(AgreementsDashboardRow, {
                            agreement: agreement,
                            hasAccountantAccess: isVendor && businessesWithAccountantAccess.has(relationshipSummaryModel.customerBusinessId(agreement)),
                            isVendor: isVendor,
                            index: index,
                            onVerifyPaymentMethod: onVerifyPaymentMethod,
                            onEditAgreement: onEditAgreement,
                            onDownloadPDF: onDownloadPDF,
                            onDuplicateAgreement: onDuplicateAgreement,
                            onTerminateAgreement: onTerminateAgreement,
                            onEnterAccountantAccess: enterAccountantAccess,
                            onRenameAgreement: onRenameAgreement,
                            onChangeAssignee: changeAssignee
                        }, agreement.id)) : /*#__PURE__*/ _jsx(PageEmptyState, {
                        illustration: /*#__PURE__*/ _jsx(NoAgreementsIcon, {}),
                        title: "No agreements match your request",
                        message: "Try adjusting your search",
                        testId: "agreements-empty-table"
                    })
                ]
            })
        });
    const hasActiveFilters = Object.values(FilterType).some((type)=>filtersConfig[type].hasValue(filters[type]));
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                source: "mgmt",
                eventName: PusherEvent.PaymentMethod,
                callback: (param)=>{
                    let { payload } = param;
                    if (payload.type === PaymentMethodEventType.PaymentMethodUpdated) {
                        refresh();
                    }
                }
            }),
            data.length > 0 || hasActiveFilters || search.length || isRefreshing ? renderAgreementsPage() : renderEmptyPage()
        ]
    });
}
export function AgreementsDashboard() {
    const { api } = useStoreActions();
    const businessId = useStore(selectors.user.businessId);
    const isVendor = useStore(selectors.user.isVendor);
    const shouldRefresh = useRef(false);
    const refreshRef = useRef(()=>{});
    const { urlState, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        agreementStatus: {
            multi: true,
            applyDefaultOnlyOnMount: true,
            defaultValue: defaultAgreementStatusFilter
        },
        effectiveDate: {
            defaultValue: ''
        },
        lastBillingDate: {
            defaultValue: ''
        },
        paymentMethod: {
            multi: true
        },
        completion: {
            multi: true
        },
        recurrence: {
            multi: true
        },
        hasCredit: {
            multi: true
        },
        automatic: {
            multi: true
        },
        billingTrigger: {
            multi: true
        },
        serviceNames: {
            multi: true
        },
        servicePriceIncreaseState: {
            multi: true
        },
        hasAmendments: {
            multi: true
        }
    });
    const { search } = urlState;
    const deserializedFilters = useMemo(()=>deserializeFiltersFromURL(urlState), [
        urlState
    ]);
    async function refreshData() {
        if (!businessId) return Promise.resolve([]);
        return api.relationship.getSummaries({
            businessId,
            type: 'agreement',
            isVendor,
            search,
            filters: deserializedFilters
        });
    }
    useSubscribeToBillingObservable(async ()=>{
        refreshRef.current();
    });
    useSetDocumentTitle(title);
    useEffect(()=>{
        if (shouldRefresh.current) refreshRef.current();
    }, [
        urlState
    ]);
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: ()=>refreshData(),
        renderLoader: ()=>/*#__PURE__*/ _jsx(PageLoadingState, {
                header: title,
                loading: true
            }),
        renderError: (param)=>{
            let { refresh } = param;
            return /*#__PURE__*/ _jsx(PageErrorState, {
                title: title,
                onRefresh: refresh
            });
        },
        renderChild: (param)=>{
            let { data, loading, refresh } = param;
            refreshRef.current = refresh;
            function refreshOnUrlStateChange() {
                shouldRefresh.current = true;
            }
            return /*#__PURE__*/ _jsx(ActualAgreementsDashboard, {
                data: data,
                isRefreshing: loading,
                refresh: refresh,
                search: search,
                filters: deserializedFilters,
                onChangeFilters: (changes)=>{
                    refreshOnUrlStateChange();
                    const serialized = {};
                    for (const [type, value] of Object.entries(changes)){
                        serialized[type] = serializeFilter(type, value);
                    }
                    changeUrlState(serialized);
                },
                onChangeSearch: (search)=>{
                    refreshOnUrlStateChange();
                    changeUrlState({
                        search
                    });
                }
            });
        }
    });
}
